import { createGameStore, type PersistedGameStore } from '@game-libs/generic-game-state';
import { registerStoreReset } from './index';
import { TEST_INITIAL_VALUES } from '../../../e2e/utils/constants';
import { GAME_LEVEL_IDS, type GameLevelId } from '../../types/game-levels';

/** Expected save schema version (was on GUIGameState; now persisted only via this store + version check on raw blob). */
export const EXPECTED_SAVE_VERSION = import.meta.env.VITE_SAVE_VERSION || '0.0.1';

// Playwright sets webdriver so ticks run faster in e2e (same rule as former initial-state).
const isE2ETestRuntime = typeof window !== 'undefined' && window.navigator.webdriver === true;

/** Default tick duration in ms (50ms in e2e, 1000ms otherwise). */
export const INITIAL_TICK_LENGTH_MS = isE2ETestRuntime ? TEST_INITIAL_VALUES.TICK_LENGTH : 1000;

/** Warriors / wizards / elves removed when the player picks "send many units" during invasion (totals `unitsCountMany`). */
export type InvasionManyUnitsSentCounts = {
  warriors: number;
  wizards: number;
  elves: number;
};

export type GameStatusState = {
  /** Matches VITE_SAVE_VERSION; used for migration / mismatch detection with the main save blob. */
  saveVersion: string;
  /** Game loop interval in ms (cheats and e2e can change this). */
  tickLength: number;
  /** Buy the maximum affordable units/upgrades in one click when true. */
  buyMax: boolean;
  /** When false, secondary HUD (timed events, generators list, stats) is hidden for a cleaner view. */
  showUI: boolean;

  dragonName: string;
  stunEffectTickDurationRemaining: number;
  playedIntro: boolean;
  playedCoupDeGrace: boolean;
  won: boolean;
  metBigDragon: boolean;
  popeVisited: boolean;
  invasionStarted: boolean;
  invasionEnded: boolean;
  lastInvasionManyUnitsSent: InvasionManyUnitsSentCounts | null;
  dummyKilled: boolean;
  shownFirstManaOutDialog: boolean;
  /** Beating / clearing a canonical level (extend when adding GameLevelId values). */
  levelCompletion: Record<GameLevelId, boolean>;
};

export type GameStatusActions = {
  setDragonName: (name: string) => void;
  setStunEffectTickDurationRemaining: (ticks: number) => void;
  markIntroPlayed: () => void;
  markCoupDeGracePlayed: () => void;
  markWon: () => void;
  markMetBigDragon: () => void;
  markPopeVisited: () => void;
  markInvasionStarted: () => void;
  markInvasionEnded: () => void;
  setLastInvasionManyUnitsSent: (counts: InvasionManyUnitsSentCounts | null) => void;
  markDummyKilled: () => void;
  markShownFirstManaOutDialog: () => void;
  markLevelComplete: (levelId: GameLevelId) => void;
  /** Bulk-sync from the reducer so Zustand persist matches game flow flags. */
  sync: (patch: Partial<GameStatusState>) => void;
  toggleBuyMax: () => void;
  toggleShowUI: () => void;
  reset: () => void;
};

export type GameStatusStore = GameStatusState & GameStatusActions;

const INITIAL_STATE: GameStatusState = {
  saveVersion: EXPECTED_SAVE_VERSION,
  tickLength: INITIAL_TICK_LENGTH_MS,
  buyMax: false,
  showUI: true,

  dragonName: 'smallDragon',
  stunEffectTickDurationRemaining: 0,
  playedIntro: false,
  playedCoupDeGrace: false,
  won: false,
  metBigDragon: false,
  popeVisited: false,
  invasionStarted: false,
  invasionEnded: false,
  lastInvasionManyUnitsSent: null,
  dummyKilled: false,
  shownFirstManaOutDialog: false,
  levelCompletion: {
    tutorial: false,
    dummy: false,
    mainGame: false,
  },
};

/**
 * Older `game-status` JSON may omit `levelCompletion` or use a partial object; merge keeps every
 * `GameLevelId` key defined so `markLevelComplete` never runs on undefined.
 */
function mergePersistedGameStatus(persisted: unknown, current: GameStatusStore): GameStatusStore {
  if (!persisted || typeof persisted !== 'object' || Array.isArray(persisted)) {
    return current;
  }
  const record = persisted as Record<string, unknown>;

  const levelCompletion: Record<GameLevelId, boolean> = { ...INITIAL_STATE.levelCompletion };
  const rawLevelCompletion = record.levelCompletion;
  if (rawLevelCompletion && typeof rawLevelCompletion === 'object' && !Array.isArray(rawLevelCompletion)) {
    for (const levelId of GAME_LEVEL_IDS) {
      const value = (rawLevelCompletion as Record<string, unknown>)[levelId];
      if (typeof value === 'boolean') {
        levelCompletion[levelId] = value;
      }
    }
  }

  const patch: Record<string, unknown> = {};
  (Object.keys(INITIAL_STATE) as (keyof GameStatusState)[]).forEach((stateKey) => {
    // Session-only: never apply a saved value (legacy blobs may still contain showUI).
    if (stateKey === 'levelCompletion' || stateKey === 'showUI') {
      return;
    }
    const incoming = record[stateKey];
    const templateValue = INITIAL_STATE[stateKey];
    if (incoming !== undefined && typeof templateValue === typeof incoming) {
      patch[stateKey] = incoming;
    }
  });

  return {
    ...current,
    ...(patch as Partial<GameStatusState>),
    levelCompletion,
    showUI: INITIAL_STATE.showUI,
  };
}

/** Omit session-only fields from the persisted JSON blob. */
function partializeGameStatusForPersist(state: GameStatusStore): Omit<GameStatusState, 'showUI'> {
  const patch: Record<string, unknown> = {};
  (Object.keys(INITIAL_STATE) as (keyof GameStatusState)[]).forEach((stateKey) => {
    if (stateKey === 'showUI') {
      return;
    }
    patch[stateKey] = state[stateKey];
  });
  return patch as Omit<GameStatusState, 'showUI'>;
}

export const useGameStatusStore: PersistedGameStore<GameStatusStore> = createGameStore<GameStatusStore>(
  {
    saveKey: 'game-status',
    persistConfig: {
      merge: (persisted, current) => mergePersistedGameStatus(persisted, current as GameStatusStore),
      partialize: (state) => partializeGameStatusForPersist(state as GameStatusStore),
    },
  },
  (set) => ({
    ...INITIAL_STATE,

    setDragonName: (dragonName) => set({ dragonName }),

    setStunEffectTickDurationRemaining: (ticks) =>
      set({ stunEffectTickDurationRemaining: ticks }),

    markIntroPlayed: () => set({ playedIntro: true }),
    markCoupDeGracePlayed: () => set({ playedCoupDeGrace: true }),
    markWon: () => set({ won: true }),
    markMetBigDragon: () => set({ metBigDragon: true }),
    markPopeVisited: () => set({ popeVisited: true }),
    markInvasionStarted: () => set({ invasionStarted: true }),
    markInvasionEnded: () => set({ invasionEnded: true }),
    setLastInvasionManyUnitsSent: (counts) => set({ lastInvasionManyUnitsSent: counts }),
    markDummyKilled: () => set({ dummyKilled: true }),
    markShownFirstManaOutDialog: () => set({ shownFirstManaOutDialog: true }),

    markLevelComplete: (levelId) =>
      set((s) => ({
        levelCompletion: { ...s.levelCompletion, [levelId]: true },
      })),

    sync: (patch) => set(patch),

    toggleBuyMax: () => set((s) => ({ buyMax: !s.buyMax })),

    toggleShowUI: () => set((s) => ({ showUI: !s.showUI })),

    reset: () => set(INITIAL_STATE),
  }),
);

registerStoreReset(() => useGameStatusStore.getState().reset());
